import React, { createContext, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import Pusher from 'pusher-js';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos sin actividad → desconectar

const PusherContext = createContext(null);

export function PusherProvider({ children }) {
    const pusherRef = useRef(null);
    const idleTimerRef = useRef(null);

    const getPusher = useCallback(() => {
        if (!pusherRef.current) {
            pusherRef.current = new Pusher(
                process.env.REACT_APP_PUSHER,
                { cluster: process.env.REACT_APP_PUSHER_CLUSTER }
            );
        }
        return pusherRef.current;
    }, []);

    // Pusher reutiliza el mismo objeto de canal si ya está suscripto
    const getChannel = useCallback((channelName) => {
        return getPusher().subscribe(channelName);
    }, [getPusher]);

    useEffect(() => {
        function disconnect() {
            const p = pusherRef.current;
            if (p && p.connection.state !== 'disconnected') {
                p.disconnect();
            }
        }

        function connect() {
            const p = pusherRef.current;
            if (p && p.connection.state === 'disconnected') {
                p.connect();
            }
        }

        function resetIdleTimer() {
            clearTimeout(idleTimerRef.current);
            connect();
            idleTimerRef.current = setTimeout(disconnect, IDLE_TIMEOUT_MS);
        }

        function handleVisibilityChange() {
            if (document.hidden) {
                // Tab oculto o minimizado → desconectar inmediatamente
                clearTimeout(idleTimerRef.current);
                disconnect();
            } else {
                // Tab visible nuevamente → reconectar y resetear timer
                resetIdleTimer();
            }
        }

        const activityEvents = ['mousemove', 'keydown', 'click', 'touchstart'];
        document.addEventListener('visibilitychange', handleVisibilityChange);
        activityEvents.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));

        // Iniciar timer de inactividad
        resetIdleTimer();

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            activityEvents.forEach(e => document.removeEventListener(e, resetIdleTimer));
            clearTimeout(idleTimerRef.current);
            if (pusherRef.current) {
                pusherRef.current.disconnect();
                pusherRef.current = null;
            }
        };
    }, []);

    const value = useMemo(() => ({ getChannel }), [getChannel]);

    return (
        <PusherContext.Provider value={value}>
            {children}
        </PusherContext.Provider>
    );
}

/**
 * Hook para suscribirse a un canal/evento de Pusher.
 * Usa la instancia centralizada — no crea nuevas conexiones.
 *
 * @param {string} channelName - Nombre del canal (ej: 'operations-channel')
 * @param {string} eventName   - Nombre del evento (ej: 'pending')
 * @param {function} handler   - Función a ejecutar cuando llega el evento
 */
export function usePusherChannel(channelName, eventName, handler) {
    const context = useContext(PusherContext);
    // Ref para que el handler siempre sea el más reciente sin necesidad de re-suscribir
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!context) return;
        const channel = context.getChannel(channelName);
        const stableHandler = (data) => handlerRef.current(data);
        channel.bind(eventName, stableHandler);
        return () => {
            channel.unbind(eventName, stableHandler);
        };
    }, [channelName, eventName, context]);
}

export default PusherContext;
